import { z } from "zod";
import {
    zHeygenEngine,
    type HeygenAspectRatio,
    type HeygenAvatarType,
    type HeygenEngine,
    type SelectUserAvatar,
    type SelectUserAvatarLook,
} from "@yedalabs/schema";

export type { HeygenAvatarType, HeygenEngine };

/** UI shape — Date columns dropped since JSON serializes them to string. */
export type Avatar = Omit<
    SelectUserAvatar,
    "userId" | "createdAt" | "updatedAt"
>;
export type AvatarLook = Omit<
    SelectUserAvatarLook,
    "createdAt" | "updatedAt"
>;

/** Group + its looks, eagerly joined for the editor sidebar. */
export interface AvatarWithLooks extends Avatar {
    looks: AvatarLook[];
}

export type SceneAvatarCorner =
    | "bottom-right"
    | "bottom-left"
    | "top-right"
    | "top-left";

export interface SceneAvatarBackground {
    type: "color" | "image";
    value?: string;
    assetId?: string;
    url?: string;
}

/**
 * Free position + size for the avatar, in % of the scene canvas.
 *
 * `x`, `y` are the TOP-LEFT corner of the avatar box, not the center —
 * matches how absolute-positioned elements work and keeps the math trivial
 * when handling resize from any corner. `width` drives sizing; height is
 * derived from `aspectRatio` so changing aspect doesn't break the position.
 *
 * `rotation` is in degrees (0 = upright).
 */
export const sceneAvatarTransformSchema = z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    aspectRatio: z.number().optional(),
    rotation: z.number().optional(),
});
export type SceneAvatarTransform = z.infer<typeof sceneAvatarTransformSchema>;

export const sceneAvatarShadowSchema = z.enum(["none", "soft", "strong"]);
export type SceneAvatarShadow = z.infer<typeof sceneAvatarShadowSchema>;

export const sceneAvatarStyleSchema = z.object({
    /**
     * CSS `border-radius` percentage applied to the avatar box (0 = square).
     * On a square box 50 = a full circle, which is the editor default when
     * unset; the toolbar slider is capped at 50.
     */
    borderRadius: z.number().optional(),
    ringWidth: z.number().optional(),
    ringColor: z.string().optional(),
    shadow: sceneAvatarShadowSchema.optional(),
    opacity: z.number().optional(),
    /** Higher renders on top among Layer-2 items. Default 0. */
    zIndex: z.number().optional(),
});
export type SceneAvatarStyle = z.infer<typeof sceneAvatarStyleSchema>;

export const sceneAvatarResolutionSchema = z.enum(["720p", "1080p", "4k"]);
export type SceneAvatarResolution = z.infer<typeof sceneAvatarResolutionSchema>;

// Avatar output aspect ratio lives in `@yedalabs/schema` as
// `zHeygenAspectRatio` / `HeygenAspectRatio` (also the shape of the
// `LomdaAvatarJobTable.aspectRatio` column) — import it from there directly.

// Re-export so consumers building per-engine schemas don't need a separate
// @yedalabs/schema import just to enum the engine field.
export { zHeygenEngine };

/**
 * Per-scene avatar configuration stored inside `LomdaScene.avatar`. All fields
 * are owned by the editor; the render pipeline reads them.
 *
 * Audio is supplied by the lomda's existing TTS flow, not by HeyGen — so no
 * voice/speech fields live on this config.
 *
 * `video` and `_fallback` are populated by the monorepo (NOT the editor) when
 * building the Pass-2 render payload, after HeyGen completes the per-scene
 * avatar generation.
 */
export interface LomdaSceneAvatar {
    /** HeyGen look id (or group id, for un-imported public avatars). */
    lookId: string;
    /**
     * UserAvatarTable.id when the avatar was created in our DB. Undefined for
     * HeyGen public-library avatars assigned directly without import.
     */
    avatarRowId?: string;
    /**
     * Preview image used by the editor canvas. Embedded so public avatars (no
     * DB row) can render without an extra lookup.
     */
    previewImageUrl?: string;
    /** Display name for tooltips / labels. */
    displayName?: string;

    /** Free positioning + sizing. Takes precedence over `corner`/`size`. */
    transform?: SceneAvatarTransform;
    /** Visual styling (shape, ring, shadow, opacity, z). */
    style?: SceneAvatarStyle;

    /** @deprecated Use `transform` instead. Kept for back-compat reads. */
    corner?: SceneAvatarCorner;
    /** @deprecated Use `transform.width` (in % of scene) instead. */
    size?: number;

    engine?: HeygenEngine;
    motionPrompt?: string;
    expressiveness?: "low" | "medium" | "high";

    background?: SceneAvatarBackground | null;
    resolution?: "720p" | "1080p" | "4k";
    aspectRatio?: HeygenAspectRatio;

    /**
     * Public R2 URL of the HeyGen-generated avatar mp4 for this scene.
     * Set by the monorepo when building the Pass-2 render payload; never set
     * by the editor.
     */
    video?: string;
    /**
     * Set when the HeyGen job for this scene failed and Pass 2 should fall
     * back to a static image (the look's preview) instead of a video.
     */
    _fallback?: "preview-image";
}

export function avatarFromDbRow(row: SelectUserAvatar): Avatar {
    const {
        userId: _userId,
        createdAt: _createdAt,
        updatedAt: _updatedAt,
        ...rest
    } = row;
    return rest;
}

export function lookFromDbRow(row: SelectUserAvatarLook): AvatarLook {
    const { createdAt: _createdAt, updatedAt: _updatedAt, ...rest } = row;
    return rest;
}

export function resolveDefaultLook(avatar: AvatarWithLooks): AvatarLook | null {
    if (avatar.looks.length === 0) return null;
    if (avatar.defaultLookId) {
        const match = avatar.looks.find(
            (l) => l.heygenLookId === avatar.defaultLookId
        );
        if (match) return match;
    }
    return avatar.looks[0];
}

/**
 * Avatar V is HeyGen's higher-quality engine. A look only advertises it in
 * `supportedEngines` AFTER its first video has been generated (HeyGen enables
 * V lazily on first render), so a freshly-created look reports only avatar_iv.
 */
export function lookSupportsV(look: AvatarLook | null | undefined): boolean {
    return look?.supportedEngines?.includes("avatar_v") ?? false;
}

/** True when any of the avatar's looks can render with Avatar V. */
export function avatarSupportsV(avatar: AvatarWithLooks): boolean {
    return avatar.looks.some(lookSupportsV);
}

/**
 * Engines our render pipeline can actually submit to HeyGen. A look that
 * advertises none of these (empty/missing engine list) can't be rendered, so
 * the editor hides it.
 */
const RENDERABLE_ENGINES: HeygenEngine[] = ["avatar_iv", "avatar_v"];

/** True when the look advertises at least one engine we can render with. */
export function hasRenderableEngine(
    engines: HeygenEngine[] | null | undefined
): boolean {
    return engines?.some((e) => RENDERABLE_ENGINES.includes(e)) ?? false;
}
